import { USState } from '@zorro/clients';
import {
  parseDateEnLocale,
  parseDateTimeEnLocale,
} from '@zorro/shared/formatters';

import { parseMaybeEnumValue } from './enum';

const ORDINAL_SUFFIXES: Record<number, string> = { 1: 'st', 2: 'nd', 3: 'rd' };

export const formatDayOfMonthToOrdinal = (day: number): string => {
  const isTeen = day >= 11 && day <= 13;
  return `${day}${isTeen ? 'th' : (ORDINAL_SUFFIXES[day % 10] ?? 'th')}`;
};

export const upperCaseFirstLetter = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const upperCaseAllFirstLetters = (str: string) => {
  const arr = str.split(' ');
  arr.forEach((tmpStr, index) => {
    arr[index] = upperCaseFirstLetter(tmpStr);
  });
  return arr.join(' ');
};

/**
 * Replaces all UUIDs in a string with "{id}".
 */
export const replaceUuidsWithId = (str: string): string => {
  const uuidRegex =
    /\b[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}\b/gu;
  return str.replace(uuidRegex, '{id}');
};

export function extractStateCodeFromExternalID(
  externalID?: string | null
): USState | null {
  if (!externalID) return null;

  const trimmed = externalID.trim();
  if (trimmed.length < 7) return null;

  return parseMaybeEnumValue(USState, trimmed.slice(5, 7)) ?? null;
}

/**
 * The **HIOS Issuer ID** — the leading 5 digits of a HIOS plan id (see
 * {@link PLAN_ID_REGEX}). Assigned by CMS per issuer per state, so it is a genuine
 * federal identifier rather than a Zorro one, and unlike a carrier *name* it cannot
 * be typed by hand or spelled two ways.
 *
 * One carrier legitimately has several (they are per state, and per legal entity
 * within a state — Capital BlueCross has two in PA), but the reverse never happens:
 * across Zorro's plan catalogue, no issuer id maps to more than one carrier name. So
 * it is safe to key on for "which carrier is this", which is what the Enrollment
 * Copilot uses it for.
 *
 * Same parse as {@link extractStateCodeFromExternalID}, a slice earlier.
 */
export function extractIssuerIdFromExternalID(
  externalID?: string | null
): string | null {
  if (!externalID) return null;

  const issuerId = externalID.trim().slice(0, 5);
  return /^\d{5}$/u.test(issuerId) ? issuerId : null;
}

/**
 * ICHRA/ACA plan id (HIOS) format: 5 digits, 2-letter state code, 7 digits,
 * with an optional `-<1-3 digits>` extension suffix. Two-digit suffixes other
 * than `-13` denote non-individual-market variants and are rejected; `-13` and 1- or 3-digit suffixes are kept.
 * Example: `41046NY0010038` or `41046NY0010038-13`.
 */
export const PLAN_ID_REGEX = /^\d{5}[A-Z]{2}\d{7}(-(?:\d{3}|13|\d))?$/u;

// Looser than PLAN_ID_REGEX: any 1-3 digit suffix, so a hand-typed CSR variant
// (e.g. -01) still names its issuer and state.
const HIOS_PLAN_ID_ANY_SUFFIX_REGEX = /^(\d{5})([A-Z]{2})\d{7}(?:-\d{1,3})?$/u;

/**
 * The issuer id and plan state of a HIOS plan id, or `null` when the value is
 * not one (empty, a placeholder such as `IFP`, free text, or a combined
 * `"A + B"` id).
 */
export function parseHiosPlanId(
  externalId?: string | null
): { issuerId: string; state: USState } | null {
  const match = HIOS_PLAN_ID_ANY_SUFFIX_REGEX.exec(externalId?.trim() ?? '');
  if (!match) return null;

  const state = parseMaybeEnumValue(USState, match[2]);
  return state ? { issuerId: match[1], state } : null;
}

export function isPlanId(value?: string | null): boolean {
  if (!value) return false;
  return PLAN_ID_REGEX.test(value.trim());
}

/**
 * Strips the `-<1-3 digits>` extension suffix from a plan id, returning the
 * canonical 14-character form used in the plans repository.
 */
export function stripPlanIdExtension(value: string): string {
  return value.trim().split('-')[0];
}

export function getSubEntityLabel(): string {
  return 'Sub-entity 1';
}

export const isAllCapsLine = (line: string): boolean =>
  /[A-Z]/u.test(line) && line === line.toUpperCase();

export const normalizeString = (value: string): string => {
  return value.toLowerCase().replace(/[^a-z0-9]/gu, '');
};

export const normalizePhone = (value?: string | null): string | null => {
  const digitsOnly = value?.replace(/\D/gu, '');
  if (!digitsOnly) return null;

  // Strip leading '1' for US numbers (11 digits starting with 1)
  if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) {
    return digitsOnly.slice(1);
  }

  return digitsOnly;
};

export const matchString = (
  stringA?: string | null,
  stringB?: string | null
): boolean => {
  return Boolean(
    stringA && stringB && normalizeString(stringA) === normalizeString(stringB)
  );
};

export function stringComparator(s1?: string, s2?: string) {
  if (s1 && s2) {
    return s1.localeCompare(s2);
  } else if (!s1 && s2) {
    return 1;
  }
  return -1;
}

export function dateStringComparator(
  d1?: string | null,
  d2?: string | null,
  isDateTimeFormat: boolean = false
) {
  const parseEnLocale = isDateTimeFormat
    ? parseDateTimeEnLocale
    : parseDateEnLocale;

  if (d1 && d2) {
    return parseEnLocale(d1).isBefore(parseEnLocale(d2)) ? -1 : 1;
  } else if (!d1 && d2) {
    return 1;
  }
  return -1;
}
